import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import type { AttentionInboxItemDto } from '../../api/analytics';
import type { DrillView } from './drillView';
import InboxItemCard from './InboxItemCard';
import ViewRenderer from './ViewRenderer';
import { CATEGORY_BADGE, SEVERITY_DOT } from './inboxShared';

/**
 * THE CASE — the opened work item as a unit of attention (the user's rule:
 * "it can't close when navigating back; it should be sticky and not let you get
 * away until you select one or another status").
 *
 * - Opening a work item starts a CASE: an INLINE panel inside the Darbai tab
 *   (owner rule 2026-09-10: the case locks ITS TAB, never the whole app —
 *   navigation, other tabs and the assistant chat stay usable around it).
 * - Information gathering happens INSIDE: linked items (KUR chips, clickable
 *   document numbers) drill into an internal stack with „← Atgal" — back and
 *   forth freely, the case never leaves.
 * - The case CANNOT be dismissed: no outside-click close, no Escape, no ✗.
 *   The only exit for an actionable item is a STATUS (Atlikta / Atidėti iki… /
 *   Ne problema). Non-actionable items (nothing to decide) get „Uždaryti".
 * - STICKINESS is the parent's job (InboxView): while unresolved, the case is
 *   remembered in sessionStorage and REOPENS whenever Darbai mounts — so even
 *   navigating away doesn't escape it; it's waiting when you return.
 */
export const STICKY_CASE_KEY = 'a5_sticky_case';

interface Props {
  item: AttentionInboxItemDto;
  aiEnabled?: boolean;
  /** A verdict was applied (or a non-actionable close) — clear the case + sticky flag. */
  onResolved: () => void;
}

export default function CaseModal({ item, aiEnabled, onResolved }: Props) {
  const { t } = useTranslation();
  const [drillStack, setDrillStack] = useState<DrillView[]>([]);
  const currentDrill = drillStack[drillStack.length - 1];
  const actionable = !!item.stableKey && !item.automated;
  const overdue = item.daysOverdue !== null && item.daysOverdue > 0;

  return (
    // Inline in the Darbai tab content — no overlay, no backdrop, nothing
    // locked around it (nav, tabs and the docked chat all stay clickable).
    <div className="bg-white border border-gray-200 rounded-2xl shadow-sm overflow-hidden flex flex-col">
      {/* Case header — who/what, always visible while drilled deep */}
      <div className="flex items-center gap-3 px-5 py-3 border-b border-gray-100 bg-white">
        <span className={`w-2 h-2 rounded-full flex-shrink-0 ${SEVERITY_DOT[item.severity ?? ''] ?? 'bg-gray-300'}`} />
        <span className={`px-2 py-0.5 text-[11px] font-semibold rounded-full flex-shrink-0 ${CATEGORY_BADGE[item.category] ?? 'bg-gray-100 text-gray-600'}`}>
          {t(`analytics.inbox.cat.${item.category}`)}
        </span>
        <span className="flex-1 text-sm font-medium text-gray-900 truncate">{item.title}</span>
        {item.amount != null && (
          <span className={`text-sm font-semibold tabular-nums flex-shrink-0 ${overdue ? 'text-red-600' : 'text-gray-700'}`}>
            {new Intl.NumberFormat('lt-LT', { style: 'currency', currency: 'EUR' }).format(item.amount)}
          </span>
        )}
        {actionable ? (
          <span className="px-2.5 py-1 text-[11px] font-semibold rounded-full bg-amber-100 text-amber-800 border border-amber-200 flex-shrink-0">
            ⏳ {t('analytics.inbox.case.stickyBadge')}
          </span>
        ) : (
          <button
            onClick={onResolved}
            className="px-3 py-1 text-xs font-medium text-gray-600 bg-gray-50 border border-gray-200 rounded-lg hover:border-gray-400 flex-shrink-0"
          >
            {t('analytics.inbox.case.close')}
          </button>
        )}
      </div>

      {/* Body: the case itself, or the drilled item (info gathering) inside it */}
      <div>
        {currentDrill ? (
          <div className="p-4">
            <button
              onClick={() => setDrillStack(stack => stack.slice(0, -1))}
              className="mb-3 inline-flex items-center gap-1 px-3 py-1.5 text-sm font-medium text-gray-700 bg-gray-50 border border-gray-300 rounded-lg hover:border-gray-500"
            >
              ← {t('analytics.inbox.case.backToCase')}
            </button>
            <ViewRenderer
              view={currentDrill}
              onDrill={view => setDrillStack(stack => [...stack, view])}
              aiEnabled={aiEnabled}
            />
          </div>
        ) : (
          <div className="p-5">
            <InboxItemCard
              item={item}
              onDrill={view => setDrillStack([view])}
              lockOpen
              aiEnabled={aiEnabled}
              onResolved={onResolved}
            />
          </div>
        )}
      </div>
    </div>
  );
}
